import { ReactNode, forwardRef } from "react";
import logoUrl from "@/assets/logo.png";

interface PrintableReportProps {
  title: string;
  filterSummary?: string;
  meta?: Array<{ label: string; value: string }>;
  children: ReactNode;
}

/**
 * Shared printable wrapper for attendance reports.
 * Uses native window.print() — see mem://technical/print-logic.
 * Hidden on screen; only visible during print via @media print.
 */
export const PrintableReport = forwardRef<HTMLDivElement, PrintableReportProps>(
  ({ title, filterSummary, meta, children }, ref) => {
    return (
      <div ref={ref} className="hidden print:block print-area">
        <style>{`
          @media print {
            @page { size: A4 landscape; margin: 12mm; }
            body * { visibility: hidden; }
            .print-area, .print-area * { visibility: visible; }
            .print-area { position: absolute; left: 0; top: 0; width: 100%; }
            .print-area table { width: 100%; border-collapse: collapse; font-size: 10pt; }
            .print-area th, .print-area td { border: 1px solid #333; padding: 4px 6px; text-align: left; vertical-align: middle; }
            .print-area thead { background: #f3f4f6; }
            .print-area .print-header { border-bottom: 2px solid #e3833a; margin-bottom: 12px; padding-bottom: 8px; }
            .print-area .print-meta { margin: 8px 0 12px; font-size: 10pt; }
            .print-area .print-meta div { padding: 2px 0; }
            .print-area .print-meta .label { display: inline-block; min-width: 90px; color: #555; font-weight: 600; }
            .print-area .session-page { page-break-after: always; page-break-inside: avoid; break-inside: avoid; }
            .print-area .session-page:last-child { page-break-after: auto; }
            .no-print { display: none !important; }
          }
        `}</style>
        <div className="print-header flex items-center gap-4">
          <img src={logoUrl} alt="St. David College" style={{ height: 56 }} />
          <div>
            <div style={{ fontSize: "14pt", fontWeight: 700 }}>
              ST. DAVID COLLEGE OF HEALTH SCIENCES
            </div>
            <div style={{ fontSize: "11pt", fontWeight: 600, color: "#e3833a" }}>{title}</div>
            {filterSummary && (
              <div style={{ fontSize: "9pt", color: "#555" }}>{filterSummary}</div>
            )}
            <div style={{ fontSize: "8pt", color: "#777" }}>
              Generated: {new Date().toLocaleString()}
            </div>
          </div>
        </div>
        {meta && meta.length > 0 && (
          <div className="print-meta">
            {meta.map((m) => (
              <div key={m.label}>
                <span className="label">{m.label}:</span> <span>{m.value}</span>
              </div>
            ))}
          </div>
        )}
        {children}
      </div>
    );
  }
);
PrintableReport.displayName = "PrintableReport";
